import { common } from './common';
import * as yup from 'yup';
import { YupMessage } from 'types';
import { validateMobile, validateLandlineNumber } from 'utils/validation';
import {
	defaultMax255CharSpiel,
	defaultMax255AlphaSpiel,
	defaultOnlyAlphaChar,
	yupValidateEmails,
} from 'utils/formSchemas/common';
import { noWhiteSpace } from 'utils/common';

export type product_contact_detail = {
	city_id?: string;
	name?: string;
	position?: string;
	department?: string;
	email?: string[];
	tel_no?: string[];
	mobile_no?: string[];
	area_handled?: string;
} & common;

export const product_contact_detail_schema = yup.object().shape({
	name: yup
		.string()
		.nullable()
		.label('Partner Contact Name')
		.required()
		.matches(/^(?!\s+$).*/, {
			message: `Input Partner Contact Name.`,
		})
		.matches(/^(?!\s+$)/, {
			message: `Input Partner Contact Name.`,
		})
		.max(255, defaultMax255AlphaSpiel)
		.matches(/^[a-z|A-Z|0-9\s]{1,255}$/, {
			excludeEmptyString: false,
			message: defaultOnlyAlphaChar,
		})
		.test(noWhiteSpace()),
	position: yup
		.string()
		.nullable()
		.label('Position')
		.required()
		.max(255, defaultMax255CharSpiel)
		.test(noWhiteSpace()),
	department: yup
		.string()
		.nullable()
		.label('Department')
		.required()
		.max(255, defaultMax255CharSpiel)
		.matches(/^[a-z|A-Z|0-9\s]{1,255}$/, {
			excludeEmptyString: false,
			message: defaultOnlyAlphaChar,
		})
		.test(noWhiteSpace()),
	email: yup
		.array()
		.nullable()
		.of(yup.string().nullable())
		.ensure()
		.label('Email Address')
		.min(1, ({ label }) => `Input ${label}`)
		.test('isValidEmail', yupValidateEmails),
	tel_no: yup
		.array()
		.nullable()
		.of(yup.string().nullable())
		.optional()
		.label('Telephone Number')
		.test(
			'isValidLandlineNumber',
			({ value }: YupMessage): string => {
				const count = value?.filter(
					(a: string) => !validateLandlineNumber(a || '')
				).length;

				const totalLength = value
					? value.reduce(
							(acc: number, val: string) => acc + (val ? val.length : 0),
							0
					  )
					: 0;

				if (totalLength >= 255) {
					return 'You can only input a max of 255 characters.';
				}
				if (count >= 1) {
					return `${count} ${
						count > 1 ? 'inputs are' : 'input is'
					} invalid telephone number format`;
				}
				return '';
			},
			(v: string[] | undefined): boolean => {
				if (!v) return true;
				return v.every((a) => validateLandlineNumber(a || '', v.length));
			}
		),
	mobile_no: yup
		.array()
		.nullable()
		.of(yup.string().nullable())
		.ensure()
		.label('Mobile Number')
		.min(1, ({ label }) => `Input ${label}`)
		.test(
			'isValidMobileNumber',
			({ value }: YupMessage): string => {
				const count = value?.filter(
					(a: string) => !validateMobile(a || '')
				).length;

				const totalLength = value
					? value.reduce(
							(acc: number, val: string) => acc + (val ? val.length : 0),
							0
					  )
					: 0;

				if (totalLength >= 255) {
					return 'You can only input a max of 255 characters.';
				}
				if (!count) return '';

				return 'Mobile number should be in +639XXXXXXXXX format.';
			},
			(v: string[] | any): boolean => {
				if (!v) return true;
				return v.every((a) => validateMobile(a || '', v.length));
			}
		),
	area_handled: yup
		.string()
		.nullable()
		.label('Area Handled')
		.notRequired()
		.nullable(true),
});
